import { DatePicker, DayOfWeek, Stack, TimePicker } from "@fluentui/react"
import { strings } from "../../../localization/strings"
import { formatTime } from "../../../core/helpers/date-time"
import { onRenderLabel } from "../../renders/onRenderLabel"
import { Noop } from "react-hook-form"
import { useCallback } from "react"

type TProps = {
    title: string
    label: string
    placeholder: string
    value: Date
    onChange: (...event: any[]) => void
    onBlur: Noop
    required?: boolean
    showUtc?: boolean
}

function isValidDate(d?: Date | null) {
    if( !d)
        return false;
    return !Number.isNaN(d.valueOf())
}

function getDefaultTime() {
    const d = new Date()
    return new Date( Date.UTC( d.getFullYear(), d.getMonth(), d.getDate(), 0, 0))
}

const DateTimePicker: React.FC<TProps> = props => {

    const updateTimeUtc = useCallback((prevDatetime: Date, newDatetimePart: Date | null | undefined, timeUpdate?: boolean) => {
        const newDate = (prevDatetime && isValidDate(prevDatetime)) ? new Date(prevDatetime) : new Date();
        if (!prevDatetime) {
            // if no previous time, reset to 00:00:00 as local time
            newDate.setHours(0);
            newDate.setMinutes(0);
            newDate.setSeconds(0, 0);
        }

        if( !isValidDate(newDatetimePart)) {
            props.onChange(newDate)
            return
        }

        if (timeUpdate) {
            // update time part only
            if (newDatetimePart) {
                newDate.setHours(newDatetimePart.getHours());
                newDate.setMinutes(newDatetimePart.getMinutes());
            }
        }
        else {
            // update date part only
            if (newDatetimePart) {
                newDate.setFullYear(newDatetimePart.getFullYear());
                newDate.setMonth(newDatetimePart.getMonth());
                newDate.setDate(newDatetimePart.getDate());
            }
        }
        props.onChange( newDate)
    }, [props])

    return (
        <Stack>
            <Stack horizontal verticalAlign="end" tokens={{ childrenGap: 16 }}>
                <Stack.Item grow>
                    {onRenderLabel({
                        title: props.title,
                        label: props.label,
                        required: props.required
                    })}
                    <DatePicker
                        firstDayOfWeek={DayOfWeek.Monday}
                        value={props.value}
                        onSelectDate={(date) => updateTimeUtc(props.value, date)}
                        formatDate={(date?: Date) => date ? date.toLocaleDateString() : ''}
                        style={{ width: 294 }}
                        onBlur={props.onBlur}
                        placeholder={props.placeholder}
                        strings={strings.DAY_PICKER}
                    />
                </Stack.Item>
                <Stack.Item>
                    <TimePicker
                        onFormatDate={formatTime}
                        onChange={(_ev, time) => updateTimeUtc(props.value, time, true)}
                        disabled={!props.value}
                        useComboBoxAsMenuWidth
                        styles={{ optionsContainer: { maxHeight: 290 } }}
                        style={{ width: 130, paddingBottom: 5 }}
                        value={isValidDate(props.value) ? props.value : getDefaultTime()}
                    />
                </Stack.Item>
            </Stack>
            { props.showUtc && (<Stack.Item>
                {props.value?.toUTCString()}
            </Stack.Item> )}
        </Stack>
    )
}

export default DateTimePicker